import React, { useRef } from 'react';
import { Field, Form } from 'react-final-form';
import PropTypes from 'prop-types';
import { Button } from '@unite-us/app-components';
import { useFeatureFlag } from 'common/hooks';
import { AUTHORIZATION_STATUSES } from 'common/Status/authorizationConstants';
import { SelectField } from 'components/Backoffice/form/SelectField';
import { SearchableField } from 'components/Backoffice/form/SearchableField';
import { SearchableSelectField } from 'components/Backoffice/form/SearchableSelectField';
import ContractedProgramFilter from './ContractedProgramFilter';

const AllFiltersDrawer = ({
  authorizationStatus,
  close,
  filterValues,
  filterOptions,
  setClientNameFilter,
  setStatusFilter,
  setRequestIdFilter,
  setContractedProgramFilter,
  setPrimaryHealthInsuranceMemberIdFilter,
  setAssignedToFilter,
  setRequestTypeFilter,
}) => {
  const contractedProgramFilterRef = useRef(null);
  const clearFilters = (form) => {
    form.reset({});
    if (contractedProgramFilterRef.current) {
      contractedProgramFilterRef.current.setSelectedOptions([]);
    }
  };

  const onSubmit = (values) => {
    setClientNameFilter(values.clientName);
    setStatusFilter(values.status);
    setRequestIdFilter(values.requestId);
    setContractedProgramFilter(values.contractedPrograms);
    setPrimaryHealthInsuranceMemberIdFilter(values.primaryHealthInsuranceMemberId);
    setAssignedToFilter(values.assignedTo);
    setRequestTypeFilter(values.requestType);
    close();
  };

  const showAssigneeFilter = useFeatureFlag('pays-8984-auth-assign-in-review');
  const showRequestTypeFilter = useFeatureFlag('pays-5650-update-edit-authorizations');

  return (
    <Form
      initialValues={filterValues}
      onSubmit={onSubmit}
      render={({ form, handleSubmit }) => (
        <form data-testid="service-authorizations-filter-drawer" className="p-12" onSubmit={handleSubmit}>
          <div className="space-y-6 h-full">
            <div className="space-y-2">
              <h2 className="text-xl font-extrabold">Filters</h2>
              <p>Add filters to refine your search. </p>
            </div>
            <Field
              name="requestId"
              render={({ input }) => (
                <SearchableField
                  {...input}
                  className="w-full"
                  clearable
                  label="Request ID"
                  onInputChange={input.onChange}
                  placeholder={'Search for a request'}
                  searchQuery={input.value}
                />
              )}
            />
            <Field
              name="clientName"
              render={({ input }) => (
                <SearchableField
                  {...input}
                  className="w-full"
                  clearable
                  label="Client Name"
                  onInputChange={input.onChange}
                  placeholder="Search by client"
                  searchQuery={input.value}
                />
              )}
            />
            {authorizationStatus !== AUTHORIZATION_STATUSES.open &&
              authorizationStatus !== AUTHORIZATION_STATUSES.rejected && (
              <Field
                name="status"
                render={({ input }) => (
                  <SelectField
                    {...input}
                    className="w-full"
                    clearable
                    label="Status"
                    options={filterOptions.status}
                    placeholder="Choose Status"
                    onChange={({ value: val }) => input.onChange(val)}
                  />
                )}
              />
            )}
            <Field
              name="contractedPrograms"
              render={({ input }) => (
                <>
                  <ContractedProgramFilter
                    options={filterOptions.contractedProgram}
                    ref={contractedProgramFilterRef}
                    selectedValues={filterValues.contractedPrograms}
                    setContractedProgramFilter={input.onChange}
                    styles={{
                      filter: {
                        width: '100%',
                        'max-width': 'unset',
                        'box-sizing': 'border-box',
                      },
                      dropdown: {
                        'max-height': '16rem',
                      },
                    }}
                  />
                </>
              )}
            />
            <Field
              name="primaryHealthInsuranceMemberId"
              render={({ input }) => (
                <SearchableField
                  className="w-full"
                  clearable
                  label="Insurance Member ID"
                  onInputChange={input.onChange}
                  placeholder="Search by Insurance Member ID"
                  searchQuery={input.value}
                />
              )}
            />
            {showAssigneeFilter && (
              <Field
                name="assignedTo"
                render={({ input }) => (
                  <SearchableSelectField
                    {...input}
                    className="w-full"
                    clearable
                    label="Assigned To"
                    options={filterOptions.assignedTo}
                    placeholder="Choose Assigned User"
                    onChange={({ value: val }) => input.onChange(val)}
                  />
                )}
              />
            )}
            {showRequestTypeFilter && (
              <Field
                name="requestType"
                render={({ input }) => (
                  <SelectField
                    {...input}
                    className="w-full"
                    clearable
                    label="Request Type"
                    options={filterOptions.requestType}
                    placeholder="Choose request type"
                    onChange={({ value: val }) => input.onChange(val)}
                  />
                )}
              />
            )}
          </div>
          <div className="flex justify-end space-x-6 mt-12">
            <Button
              className="py-2 px-4 capitalize"
              label="Clear All"
              link
              onClick={() => clearFilters(form)}
            />
            <Button
              label="Apply Filters"
              primary
              type="submit"
            />
          </div>
        </form>
      )}
    />
  );
};

AllFiltersDrawer.propTypes = {
  authorizationStatus: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  filterValues: PropTypes.shape({
    clientName: PropTypes.string,
    contractedPrograms: PropTypes.arrayOf(PropTypes.string),
    contractedProgramName: PropTypes.string,
    primaryHealthInsuranceMemberId: PropTypes.string,
    requestId: PropTypes.string,
    status: PropTypes.string,
    requestType: PropTypes.string,
  }).isRequired,
  filterOptions: PropTypes.shape({
    contractedProgram: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    status: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    assignedTo: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    requestType: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  setAssignedToFilter: PropTypes.func.isRequired,
  setClientNameFilter: PropTypes.func.isRequired,
  setContractedProgramFilter: PropTypes.func.isRequired,
  setPrimaryHealthInsuranceMemberIdFilter: PropTypes.func.isRequired,
  setRequestIdFilter: PropTypes.func.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  setRequestTypeFilter: PropTypes.func.isRequired,
 };

export default AllFiltersDrawer;
